/**
 * PRD.218 + PRD.229 — Record a payment.
 *
 * Two modes:
 *   - **Invoice-locked** (default for callers passing `invoice`): the
 *     dialog operates against a specific invoice, shows total/paid/
 *     remaining, and enforces the no-overpayment cap. This is how the
 *     InvoicesTab drawer, BillingAcrossVisitsView row "Pay" action,
 *     and GlobalInvoicesPage row "Pay" action have always called it.
 *   - **Patient-level** (PRD.229): when `invoice` is null and
 *     `lockInvoice` is false, the dialog renders an optional invoice
 *     picker. The user can pick an invoice (then the locked-mode UX
 *     kicks in) or stay patient-level (no invoice link). Patient-level
 *     payments write `invoice_id=NULL` + `patient_id={patientId}` so
 *     `qPatientBillingAggregate` still folds them into the balance.
 *
 * Visit Date (PRD.229 §5.2/5.3) is always asked, defaults to today,
 * and overrides `paid_at` so the payment row is anchored to clinic
 * context rather than just the moment the user clicked Save.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import {
  qInvoicePaidTotalCents,
  qInvoicesByPatient,
  type InvoicePaidTotalRow,
  type InvoiceRow,
} from '@helios/data-access';
import {
  greaterThan,
  isPositive,
  parseAmountInput,
  phpFromCents,
  phpFromMajor,
  subtractMoney,
  toCents,
  type Money,
} from '@helios/domain';
import { useFormatMoney, useRegionalSettings } from '../../hooks/useFormatMoney';

// ─── Types ───

export type PaymentMethod = 'cash' | 'card' | 'bank_transfer' | 'gcash' | 'other';

const METHOD_OPTIONS: ReadonlyArray<{ value: PaymentMethod; label: string }> = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'gcash', label: 'GCash' },
  { value: 'other', label: 'Other' },
];

interface InvoiceForPayment {
  id: string;
  invoice_no: string | null;
  /** Decimal-major value from invoices.grand_total. */
  grand_total: number | null;
  status: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-selected invoice. When null + `lockInvoice` is false, the dialog shows a picker. */
  invoice: InvoiceForPayment | null;
  /**
   * PRD.229 — required so the dialog can scope the optional invoice
   * picker AND stamp invoice_payments.patient_id directly (which the
   * patient billing aggregate uses to fold in patient-level payments).
   */
  patientId: string;
  /**
   * When true (default for invoice-scoped callers), the dialog stays
   * locked to the given invoice. When false (Billing-view entry point),
   * the dialog shows an invoice picker with a patient-level option.
   */
  lockInvoice?: boolean;
  orgId: string;
  /** Called after a successful insert so callers can refresh, toast, etc. */
  onRecorded?: () => void;
}

// ─── Helpers ───

function todayISODate(): string {
  const d = new Date();
  // toISOString gives UTC; use local Y-M-D so "today" matches the user's clock.
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Translate a date input (YYYY-MM-DD) into a full ISO timestamp anchored
 * to local noon. Noon dodges DST/timezone-boundary surprises when the
 * record is read back from servers in another timezone.
 */
function visitDateToIso(date: string): string {
  const [y, m, d] = date.split('-').map((s) => parseInt(s, 10));
  if (!y || !m || !d) return new Date().toISOString();
  return new Date(y, m - 1, d, 12, 0, 0).toISOString();
}

// ─── Component ───

export function RecordPaymentDialog({
  open,
  onOpenChange,
  invoice,
  patientId,
  lockInvoice = true,
  orgId,
  onRecorded,
}: Props) {
  const { db } = usePowerSyncDb();
  const { user } = useAuth();
  const fmt = useFormatMoney();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;

  // Form fields.
  const [pickedInvoiceId, setPickedInvoiceId] = useState<string>('');
  const [amountInput, setAmountInput] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [notes, setNotes] = useState('');
  const [visitDate, setVisitDate] = useState<string>(() => todayISODate());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // PRD.229 §5.6 — Quick Pay UX: when the dialog opens with sensible
  // defaults already prefilled (amount = remaining, method = Cash,
  // date = today), the user can just press Enter to confirm. Focusing
  // the submit button on open enables that single-keystroke flow.
  const submitRef = useRef<HTMLButtonElement>(null);

  // Picker source — only loaded when the dialog is open AND no invoice was
  // pinned by the caller, so invoice-scoped opens stay cheap.
  const pickerEnabled = open && !invoice && !lockInvoice && !!patientId;
  const { data: patientInvoices = [] } = usePowerSyncQuery<InvoiceRow>(
    pickerEnabled
      ? qInvoicesByPatient(patientId)
      : {
          key: ['noop-patient-invoices-payments'] as const,
          sql: 'SELECT NULL WHERE 0',
          params: [],
          watch: [],
        },
  );

  // Effective invoice — either the one passed in by the caller (locked
  // mode) or the one the user picked from the dropdown.
  const effectiveInvoice: InvoiceForPayment | null = useMemo(() => {
    if (invoice) return invoice;
    if (!pickedInvoiceId) return null;
    const row = patientInvoices.find((r) => r.id === pickedInvoiceId);
    if (!row) return null;
    return {
      id: row.id,
      invoice_no: row.invoice_no,
      grand_total: row.grand_total ?? 0,
      status: row.status,
    };
  }, [invoice, pickedInvoiceId, patientInvoices]);

  // Live paid total for whichever invoice is currently effective.
  const { data: paidRows = [] } = usePowerSyncQuery<InvoicePaidTotalRow>(
    effectiveInvoice
      ? qInvoicePaidTotalCents(effectiveInvoice.id)
      : {
          key: ['noop-paid-total'] as const,
          sql: 'SELECT 0 AS paid_cents WHERE 0',
          params: [],
          watch: [],
        },
  );

  const grandTotal: Money = useMemo(
    () => phpFromMajor(effectiveInvoice?.grand_total ?? 0),
    [effectiveInvoice?.grand_total],
  );
  const paidSoFar: Money = useMemo(() => phpFromCents(paidRows[0]?.paid_cents ?? 0), [paidRows]);
  const remaining: Money = useMemo(
    () => subtractMoney(grandTotal, paidSoFar),
    [grandTotal, paidSoFar],
  );

  // Reset the form whenever the dialog (re)opens or the invoice changes.
  useEffect(() => {
    if (!open) return;
    setPickedInvoiceId('');
    setMethod('cash');
    setNotes('');
    setVisitDate(todayISODate());
    setSubmitting(false);
    setError(null);
    // In invoice-locked mode, default amount to the remaining balance so
    // the common "pay in full" case is one click. In patient-level mode
    // we start empty — there's no remaining to default against.
    if (invoice) {
      const remainingMajor = remaining.toRoundedUnit(2);
      setAmountInput(remainingMajor > 0 ? remainingMajor.toFixed(2) : '');
    } else {
      setAmountInput('');
    }
    // `remaining` intentionally not in deps — we only seed the amount
    // when the dialog opens or the invoice changes, not on every paid-row update.
  }, [open, invoice?.id]);

  // PRD.229 §5.6 — Focus the submit button on open so the user can
  // confirm with Enter when defaults are already correct (Quick Pay
  // flow). Modal portals through `mounted` + `visible` flags so the
  // button isn't in the DOM on the first render after `open` flips
  // true. Poll across a handful of frames until the ref is set, then
  // focus — also keeps us safe against the Modal's own focus-trap,
  // which auto-focuses the first focusable on mount.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let attempts = 0;
    function tick() {
      if (cancelled) return;
      const btn = submitRef.current;
      if (btn && !btn.disabled) {
        btn.focus();
        return;
      }
      if (attempts++ < 20) requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
    };
  }, [open]);

  const parsed = useMemo(() => parseAmountInput(amountInput), [amountInput]);

  // Validation rules:
  //   - amount must parse and be positive
  //   - if an invoice is effective, can't exceed its remaining balance
  //   - patient-level mode (no effective invoice) has no max cap
  const exceedsRemaining = !!effectiveInvoice && parsed != null && greaterThan(parsed, remaining);
  const canSubmit =
    !!db && !!patientId && !submitting && parsed != null && isPositive(parsed) && !exceedsRemaining;

  async function handleSubmit() {
    if (!db || !patientId || !parsed) return;
    if (!isPositive(parsed)) {
      setError('Amount must be greater than zero.');
      return;
    }
    if (exceedsRemaining) {
      setError(`Amount exceeds remaining balance (${fmt(remaining)}).`);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const now = new Date().toISOString();
      const paidAt = visitDateToIso(visitDate);
      const paymentCents = toCents(parsed);
      const inv = effectiveInvoice;
      const newPaidCents = inv ? toCents(paidSoFar) + paymentCents : paymentCents;
      const grandTotalCents = inv ? toCents(grandTotal) : 0;
      const fullySettled = !!inv && newPaidCents >= grandTotalCents && grandTotalCents > 0;

      await db.execute(
        `INSERT INTO invoice_payments
           (id, org_id, invoice_id, patient_id, amount_cents, method, notes, paid_at, created_at, updated_at, updated_by)
         VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          inv?.id ?? null,
          patientId,
          paymentCents,
          method,
          notes.trim() || null,
          paidAt,
          now,
          now,
          user?.uid ?? null,
        ],
      );

      // Invoice status follows balance — only meaningful when invoice-linked.
      if (inv) {
        const nextStatus = fullySettled
          ? 'paid'
          : inv.status === 'draft'
            ? 'issued'
            : (inv.status ?? 'issued');

        if (nextStatus !== inv.status) {
          await db.execute(`UPDATE invoices SET status = ?, updated_at = ? WHERE id = ?`, [
            nextStatus,
            now,
            inv.id,
          ]);
        }

        // PRD.218 §10.1 — mirror full-settlement into linked treatment records.
        if (fullySettled) {
          const linked = await db.getAll<{ id: string; amount_charged_cents: number }>(
            `SELECT id, amount_charged_cents FROM patient_treatment_records WHERE invoice_id = ?`,
            [inv.id],
          );
          for (const rec of linked) {
            await db.execute(
              `UPDATE patient_treatment_records
                  SET amount_paid_cents = ?, updated_at = ?
                WHERE id = ?`,
              [rec.amount_charged_cents, now, rec.id],
            );
          }
        }
      }

      onRecorded?.();
      onOpenChange(false);
    } catch (e) {
      console.error('[RecordPaymentDialog] insert failed', e);
      setError('Could not save payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const balanceZero = !!effectiveInvoice && !isPositive(remaining) && toCents(remaining) <= 0;

  const headerTitle = effectiveInvoice
    ? `Record Payment — ${effectiveInvoice.invoice_no ?? 'Invoice'}`
    : 'Record Payment';
  const headerDescription = effectiveInvoice
    ? `Invoice total ${fmt(grandTotal)} · Paid so far ${fmt(paidSoFar)} · Remaining ${fmt(remaining)}`
    : 'Record a patient payment. Link to a specific invoice or leave patient-level.';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={headerTitle}
      description={headerDescription}
      maxWidth={460}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--sp-2)' }}>
          <Button
            variant="ghost"
            size="md"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            ref={submitRef}
            variant="gradient"
            size="md"
            onClick={handleSubmit}
            disabled={!canSubmit || balanceZero}
          >
            {submitting ? 'Saving…' : 'Record Payment'}
          </Button>
        </div>
      }
    >
      {balanceZero ? (
        <p
          style={{
            margin: 0,
            padding: 'var(--sp-5) var(--sp-6)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
          }}
        >
          This invoice is already fully paid.
        </p>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
            padding: 'var(--sp-5) var(--sp-6)',
          }}
        >
          {/* PRD.229 — Visit Date, always shown. Anchors paid_at. */}
          <Field label="Visit Date">
            <input
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value || todayISODate())}
              style={inputStyle()}
            />
          </Field>

          {/* PRD.229 — Optional invoice picker, shown only when this dialog
              wasn't opened against a fixed invoice. */}
          {!invoice && !lockInvoice && (
            <Field label="Linked invoice (optional)">
              <select
                value={pickedInvoiceId}
                onChange={(e) => setPickedInvoiceId(e.target.value)}
                style={inputStyle()}
              >
                <option value="">— Patient-level (no invoice link) —</option>
                {patientInvoices.map((inv) => (
                  <option key={inv.id} value={inv.id}>
                    {(inv.invoice_no ?? inv.id.slice(0, 8)) +
                      (inv.date_iso ? ` · ${inv.date_iso}` : '') +
                      (inv.status ? ` · ${inv.status}` : '')}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Amount">
            <div style={{ position: 'relative' }}>
              <span
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-sm)',
                  pointerEvents: 'none',
                }}
              >
                {currencySymbol}
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => {
                  setAmountInput(e.target.value);
                  if (error) setError(null);
                }}
                placeholder="0.00"
                style={{ ...inputStyle(), paddingLeft: 24 }}
              />
            </div>
            {effectiveInvoice && (
              <HintRow>
                <button
                  type="button"
                  onClick={() => {
                    const r = remaining.toRoundedUnit(2);
                    setAmountInput(r > 0 ? r.toFixed(2) : '0');
                  }}
                  style={linkButtonStyle}
                >
                  Pay full balance ({fmt(remaining)})
                </button>
              </HintRow>
            )}
          </Field>

          <Field label="Method">
            <select
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              style={inputStyle()}
            >
              {METHOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Note (optional)">
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. OR #1234, partial deposit"
              style={inputStyle()}
            />
          </Field>

          {error && (
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-danger-text, #b91c1c)',
                background: 'var(--color-danger-bg, #fef2f2)',
                border: '1px solid var(--color-danger-border, #fecaca)',
                padding: 'var(--sp-2) var(--sp-3)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              {error}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ─── Small layout helpers ───

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 500,
          color: 'var(--text-muted)',
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function HintRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: 4,
      }}
    >
      {children}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    width: '100%',
    height: 36,
    padding: '0 12px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: 'var(--bg-surface)',
    color: 'var(--text)',
    fontSize: 'var(--font-size-sm)',
  };
}

const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--color-primary, #4f46e5)',
  fontSize: 'var(--font-size-xs)',
  cursor: 'pointer',
  padding: 0,
};
